import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useState, useMemo, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllVendors, getVendorBillsList, deleteVendorBill, approveVendorBill, bulkUploadVendorBills, resetVendorBillBulkUpload } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import Pagination from '../../../components/common/Pagination';
import * as routes from '../../../Routing/routes';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import { stripHtml } from '../../../utils/helpers/stripHtml';
import { resolveBillPaymentStatus, getPaymentStatusBadge } from '../../../utils/helpers/billPaymentStatus';
import { notify } from '../../../utils/notification';
import { canAny } from '../../../utils/helpers/can';
import { PERMISSIONS } from '../../../consts/permissions';

const STATUSES = ['Post', 'Draft'];

const DraftBillCheckbox = ({ checked, onChange, label, className = '' }) => (
    <span className={`tw-relative tw-inline-flex tw-h-5 tw-w-5 tw-flex-shrink-0 ${className}`}>
        <input
            type="checkbox"
            className="tw-absolute tw-inset-0 tw-z-10 tw-h-5 tw-w-5 tw-cursor-pointer tw-opacity-0"
            checked={checked}
            onChange={onChange}
            aria-label={label}
        />
        <span
            aria-hidden="true"
            className={`tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-border-2 tw-transition-colors ${
                checked
                    ? 'tw-border-emerald-700 tw-bg-emerald-600 tw-text-white'
                    : 'tw-border-gray-600 tw-bg-gray-100'
            }`}
        >
            {checked && <Icon icon="mdi:check-bold" className="tw-text-sm" />}
        </span>
    </span>
);

const VendorBill = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();

    // Get data from Redux store
    const {
        allVendorsData,
        allVendorsLoading,
        vendorBillsListData,
        vendorBillsListLoading,
        vendorBillsPagination,
        deleteVendorBillLoading,
        approveVendorBillLoading,
        // Bulk Excel upload
        bulkUploadVendorBillsLoading,
        bulkUploadVendorBillsResult,
    } = useSelector(state => state.VendorReducers);
    const userPermissions = useSelector(state => state.UserSocietyReducer?.permissions || []);
    const canCreateBills = canAny(userPermissions, [PERMISSIONS.VENDOR_BILL_CREATE]);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterVendor, setFilterVendor] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [viewMode, setViewMode] = useState('grid');
    const [deleteModal, setDeleteModal] = useState({ show: false, bill: null });
    // Posting a draft bill (DRAFT -> POST) locks it for payment.
    const [postModal, setPostModal] = useState({ show: false, bill: null });
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkPosting, setBulkPosting] = useState(false);

    // Bulk Excel upload — picker modal + results modal, matching the pattern
    // used on the Vendors list. Reset is dispatched on close so a subsequent
    // upload starts with a clean summary.
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadResultOpen, setUploadResultOpen] = useState(false);

    const handleDownloadBillTemplate = async () => {
        try {
            const res = await axiosInstance.get(apiEndpoints.SOCIETY_VENDOR_BILL_UPLOAD_TEMPLATE_API, { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'vendor-bill-upload-template.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            window.open(apiEndpoints.SOCIETY_VENDOR_BILL_UPLOAD_TEMPLATE_API, '_blank');
        }
    };

    const handleUploadBillSubmit = () => {
        if (!uploadFile) return;
        const societyId = selectedSocietyStorer.getSocietyId();
        dispatch(bulkUploadVendorBills({
            file: uploadFile,
            societyId,
            onSuccess: () => {
                setUploadModalOpen(false);
                setUploadFile(null);
                setUploadResultOpen(true);
                // Refresh the list so freshly uploaded bills show up.
                dispatch(getVendorBillsList({ page: currentPage, limit: itemsPerPage, societyId }));
            }
        }));
    };

    const handleCloseBillResults = () => {
        setUploadResultOpen(false);
        dispatch(resetVendorBillBulkUpload());
    };

    // Fetch all vendors on component mount
    useEffect(() => {
        dispatch(getAllVendors({ society_id: selectedSocietyStorer.getSocietyId() }));
    }, [dispatch]);

    // Fetch vendor bills when filters or pagination change
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getVendorBillsList({
            societyId,
            page: currentPage,
            limit: itemsPerPage,
            search: searchQuery,
            vendor_id: filterVendor,
            status: filterStatus
        }));
    }, [dispatch, societyId, currentPage, itemsPerPage, searchQuery, filterVendor, filterStatus, refreshKey]);

    // Map API response to bills list
    const bills = useMemo(() => {
        if (!Array.isArray(vendorBillsListData)) return [];
        return vendorBillsListData.map(bill => ({
            id: bill.bill_id || bill.id,
            billNo: bill.bill_number,
            vendorId: bill.vendor_id,
            vendor: bill.vendor?.business_name || bill.business_name || 'N/A',
            category: bill.vendor?.category?.category_name || bill.category_name || 'N/A',
            description: stripHtml(bill.description),
            amount: parseFloat(bill.amount) || 0,
            gst: parseFloat(bill.gst) || 0,
            totalAmount: parseFloat(bill.total_amount) || 0,
            billDate: bill.bill_date,
            dueDate: bill.due_date,
            status: bill.status || 'Pending',
            paidAmount: parseFloat(bill.paid_amount) || 0,
            balanceAmount: parseFloat(bill.balance_amount) || 0,
            paymentStatus: resolveBillPaymentStatus(bill),
            paidDate: bill.paid_date,
            notes: bill.notes
        }));
    }, [vendorBillsListData]);

    // Map API response to vendor list for dropdowns
    const vendorsList = useMemo(() => {
        if (!Array.isArray(allVendorsData)) return [];
        return allVendorsData.map(vendor => ({
            id: vendor.vendor_id,
            name: vendor.business_name
        }));
    }, [allVendorsData]);

    const stats = useMemo(() => {
        const totalBills = bills.reduce((sum, b) => sum + b.totalAmount, 0);
        const draftCount = bills.filter(b => b.status?.toLowerCase() === 'draft').length;
        const postCount = bills.filter(b => b.status?.toLowerCase() !== 'draft').length;
        return {
            total: bills.length,
            totalAmount: totalBills,
            draftCount,
            postCount,
        };
    }, [bills]);

    // Use server-side pagination
    const totalPages = vendorBillsPagination?.totalPages || 1;
    const totalItems = vendorBillsPagination?.total || bills.length;

    const clearFilters = () => {
        setSearchQuery('');
        setFilterVendor('');
        setFilterStatus('');
        setCurrentPage(1);
    };

    const hasActiveFilters = searchQuery || filterVendor || filterStatus;

    // Handle delete bill
    const handleDeleteBill = (bill) => {
        setDeleteModal({ show: true, bill });
    };

    // Only drafts can be posted, so only they are selectable.
    const isDraft = (status) => String(status || '').toUpperCase() === 'DRAFT';
    const draftIdsOnPage = useMemo(() => bills.filter(b => isDraft(b.status)).map(b => b.id), [bills]);
    const selectedDraftIds = useMemo(() => selectedIds.filter(id => draftIdsOnPage.includes(id)), [selectedIds, draftIdsOnPage]);
    const toggleSelected = (id) => setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    const toggleSelectAll = () => setSelectedIds(selectedDraftIds.length === draftIdsOnPage.length ? [] : draftIdsOnPage);

    const refreshBills = () => dispatch(getVendorBillsList({
        societyId, page: currentPage, limit: itemsPerPage, search: searchQuery, vendor_id: filterVendor, status: filterStatus
    }));

    // Post every ticked draft in one request. Each bill's journal entry is
    // booked server-side; the response names any bill that could not be posted.
    const handleBulkPost = async () => {
        if (selectedDraftIds.length === 0) return;
        setBulkPosting(true);
        try {
            const res = await axiosInstance.put(apiEndpoints.SOCIETY_BULK_POST_VENDOR_BILLS_API, { bill_ids: selectedDraftIds });
            if (res?.success) {
                const { postedCount = 0, posted = [], failed = [] } = res.data || {};
                if (postedCount > 0) notify('success', res.message || `${postedCount} bill(s) posted`);
                posted.filter(b => b.warning).forEach(b => notify('warning', `${b.bill_number || `Bill ${b.bill_id}`}: ${b.warning}`));
                failed.forEach(f => notify('error', `${f.bill_number || `Bill ${f.bill_id}`}: ${f.error}`));
                setSelectedIds([]);
                refreshBills();
            } else {
                notify('error', res?.message || 'Could not post the selected bills');
            }
        } catch (err) {
            notify('error', err.response?.data?.message || 'Could not post the selected bills');
        } finally {
            setBulkPosting(false);
        }
    };

    const handlePostBill = (bill) => {
        setPostModal({ show: true, bill });
    };

    const confirmPostBill = () => {
        if (!postModal.bill) return;
        dispatch(approveVendorBill({
            billId: postModal.bill.id,
            onSuccess: () => {
                setPostModal({ show: false, bill: null });
                dispatch(getVendorBillsList({
                    societyId,
                    page: currentPage,
                    limit: itemsPerPage,
                    search: searchQuery,
                    vendor_id: filterVendor,
                    status: filterStatus
                }));
            }
        }));
    };

    const confirmDeleteBill = () => {
        if (deleteModal.bill) {
            dispatch(deleteVendorBill({
                billId: deleteModal.bill.id,
                onSuccess: () => {
                    setDeleteModal({ show: false, bill: null });
                    dispatch(getVendorBillsList({
                        societyId,
                        page: currentPage,
                        limit: itemsPerPage,
                        search: searchQuery,
                        vendor_id: filterVendor,
                        status: filterStatus
                    }));
                }
            }));
        }
    };

const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
    };

    const getStatusBadge = (status) => {
        if (status?.toLowerCase() === 'draft') return 'tw-bg-amber-100 tw-text-amber-700';
        return 'tw-bg-emerald-100 tw-text-emerald-700';
    };

    const getStatusLabel = (status) => {
        return status?.toLowerCase() === 'draft' ? 'Draft' : 'Post';
    };

    const handlePrintBill = (bill) => {
        navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDOR_BILLS_VIEW.replace(':id', bill.id) + '?print=true');
    };

    const handleViewBill = (bill) => {
        navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDOR_BILLS_VIEW.replace(':id', bill.id));
    };

    const handleEditBill = (bill) => {
        navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDOR_BILLS_EDIT.replace(':id', bill.id));
    };

    const handleAddBill = () => {
        navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDOR_BILLS_ADD);
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Vendor Bills</h1>
                    <p className="tw-text-xs tw-text-gray-500">Manage vendor bills and invoices</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {canCreateBills && (
                        <button
                            onClick={() => setUploadModalOpen(true)}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-text-blue-700 tw-border tw-border-blue-200 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-50"
                            title="Bulk upload vendor bills from Excel"
                        >
                            <Icon icon="mdi:file-excel-outline" /> Bulk Upload Bills
                        </button>
                    )}
                    <button
                        onClick={handleAddBill}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" /> Add Bill
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-mb-4">
                {[
                    { icon: 'mdi:file-document-multiple', color: 'tw-bg-blue-500', value: stats.total, label: 'Total Bills', sub: formatCurrency(stats.totalAmount) },
                    { icon: 'mdi:file-check-outline', color: 'tw-bg-emerald-500', value: stats.postCount, label: 'Posted' },
                    { icon: 'mdi:file-edit-outline', color: 'tw-bg-amber-500', value: stats.draftCount, label: 'Draft' },
                ].map((stat, i) => (
                    <div key={i} className="tw-bg-white tw-rounded-lg tw-p-3 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5">
                        <div className={`tw-w-10 tw-h-10 tw-rounded-lg ${stat.color} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                            <Icon icon={stat.icon} className="tw-text-white tw-text-lg" />
                        </div>
                        <div>
                            <p className="tw-text-base tw-font-bold tw-text-gray-800 tw-leading-none">{stat.value}</p>
                            <p className="tw-text-[11px] tw-text-gray-500">{stat.label}</p>
                            {stat.sub && <p className="tw-text-[10px] tw-text-gray-400">{stat.sub}</p>}
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div className="tw-bg-white tw-rounded-lg tw-py-2.5 tw-px-3 tw-mb-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <div className="tw-flex-1 tw-min-w-[200px] tw-relative">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        placeholder="Search by bill no, vendor or description..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="tw-w-full tw-pl-8 tw-pr-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                    />
                </div>
                <select
                    value={filterVendor}
                    onChange={(e) => { setFilterVendor(e.target.value); setCurrentPage(1); }}
                    disabled={allVendorsLoading}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500 disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                >
                    <option value="">{allVendorsLoading ? 'Loading...' : 'All Vendors'}</option>
                    {vendorsList.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="">All Status</option>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {hasActiveFilters && (
                    <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1 tw-text-red-500 tw-text-xs tw-font-medium hover:tw-text-red-600">
                        <Icon icon="mdi:close" className="tw-text-sm" /> Clear
                    </button>
                )}
                <div className="tw-flex tw-border tw-border-gray-200 tw-rounded-md tw-overflow-hidden">
                    <button onClick={() => setViewMode('grid')} className={`tw-p-1.5 ${viewMode === 'grid' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500'}`}>
                        <Icon icon="mdi:view-grid" className="tw-text-sm" />
                    </button>
                    <button onClick={() => setViewMode('list')} className={`tw-p-1.5 ${viewMode === 'list' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500'}`}>
                        <Icon icon="mdi:view-list" className="tw-text-sm" />
                    </button>
                </div>
                <span className="tw-text-[11px] tw-text-gray-400 tw-ml-auto">
                    {vendorBillsListLoading ? 'Loading...' : `Showing ${bills.length} of ${totalItems} bills`}
                </span>
            </div>

            {/* Loading State */}
            {vendorBillsListLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading bills...</p>
                </div>
            )}

            {/* Grid View */}
            {/* Keep draft selection controls visible even before a bill is selected. */}
            {draftIdsOnPage.length > 0 && (
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-3 tw-px-3 tw-py-2 tw-bg-emerald-50 tw-border tw-border-emerald-200 tw-rounded-lg">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-xs tw-font-medium tw-text-emerald-900">
                            <DraftBillCheckbox
                                checked={selectedDraftIds.length === draftIdsOnPage.length}
                                onChange={toggleSelectAll}
                                label="Select all draft bills on this page"
                            />
                            Select all draft bills
                        </label>
                        <span className="tw-text-xs tw-text-emerald-800">
                            {selectedDraftIds.length} of {draftIdsOnPage.length} selected
                        </span>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                            onClick={() => setSelectedIds([])}
                            disabled={selectedDraftIds.length === 0}
                            className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                        >
                            Clear
                        </button>
                        <button
                            onClick={handleBulkPost}
                            disabled={bulkPosting || selectedDraftIds.length === 0}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-emerald-600 tw-rounded-md hover:tw-bg-emerald-700 disabled:tw-opacity-50"
                        >
                            <Icon icon={bulkPosting ? 'mdi:loading' : 'mdi:check-circle-outline'} className={bulkPosting ? 'tw-animate-spin' : ''} />
                            {bulkPosting ? 'Posting…' : `Post selected (${selectedDraftIds.length})`}
                        </button>
                    </div>
                </div>
            )}

            {!vendorBillsListLoading && viewMode === 'grid' && bills.length > 0 && (
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-3">
                    {bills.map((bill) => (
                        <div key={bill.id} className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-3">
                            {/* Header: Bill No & Status */}
                            <div className="tw-flex tw-items-start tw-justify-between tw-mb-2.5">
                                <div className="tw-flex tw-items-center tw-gap-2.5">
                                    {isDraft(bill.status) && (
                                        <DraftBillCheckbox
                                            checked={selectedIds.includes(bill.id)}
                                            onChange={() => toggleSelected(bill.id)}
                                            label={`Select draft bill ${bill.billNo}`}
                                            className="tw-mt-1"
                                        />
                                    )}
                                    <div className={`tw-w-10 tw-h-10 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${bill?.status?.toLowerCase() === 'draft' ? 'tw-bg-amber-500' : 'tw-bg-blue-500'}`}>
                                        <Icon icon="mdi:file-document-outline" className="tw-text-white tw-text-lg" />
                                    </div>
                                    <div>
                                        <p className="tw-font-semibold tw-text-gray-800 tw-text-sm">{bill.billNo}</p>
                                        <p className="tw-text-[11px] tw-text-gray-500">{bill.vendor}</p>
                                    </div>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-1">
                                    <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getStatusBadge(bill.status)}`}>
                                        {getStatusLabel(bill.status)}
                                    </span>
                                    {bill.status?.toLowerCase() !== 'draft' && (
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getPaymentStatusBadge(bill.paymentStatus)}`}>
                                            {bill.paymentStatus}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Description */}
                            {bill.description && (
                                <p className="tw-text-xs tw-text-gray-600 tw-line-clamp-1 tw-mb-2">{bill.description}</p>
                            )}

                            {/* Amount */}
                            <div className="tw-flex tw-items-center tw-justify-between tw-py-2 tw-border-t tw-border-b tw-border-gray-100 tw-mb-2">
                                <span className="tw-text-xs tw-text-gray-500">Total Amount</span>
                                <span className="tw-font-bold tw-text-gray-800 tw-text-sm">{formatCurrency(bill.totalAmount)}</span>
                            </div>

                            {/* Dates */}
                            <div className="tw-space-y-1.5 tw-mb-2">
                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                                    <Icon icon="mdi:calendar-outline" className="tw-text-gray-400 tw-text-sm" />
                                    <span>Bill: {bill.billDate ? new Date(bill.billDate).toLocaleDateString('en-IN') : '-'}</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                                    <Icon icon="mdi:calendar-clock-outline" className="tw-text-gray-400 tw-text-sm" />
                                    <span>Due: {bill.dueDate ? new Date(bill.dueDate).toLocaleDateString('en-IN') : '-'}</span>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-1 tw-pt-2 tw-border-t tw-border-gray-100">
                                <button onClick={() => handleViewBill(bill)} className="tw-p-1.5 tw-rounded hover:tw-bg-emerald-50 tw-text-gray-400 hover:tw-text-emerald-600" title="View">
                                    <Icon icon="mdi:eye-outline" />
                                </button>
                                <button onClick={() => handlePrintBill(bill)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="Print">
                                    <Icon icon="mdi:printer-outline" />
                                </button>
                                <button onClick={() => handleEditBill(bill)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600" title="Edit">
                                    <Icon icon="mdi:pencil-outline" />
                                </button>
                                {bill.status?.toLowerCase() === 'draft' && (
                                    <button onClick={() => handlePostBill(bill)} className="tw-p-1.5 tw-rounded hover:tw-bg-emerald-50 tw-text-gray-400 hover:tw-text-emerald-600" title="Post bill">
                                        <Icon icon="mdi:check-circle-outline" />
                                    </button>
                                )}
                                {bill.status?.toLowerCase() === 'draft' && (
                                    <button onClick={() => handleDeleteBill(bill)} className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-600" title="Delete">
                                        <Icon icon="mdi:delete-outline" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Grid View Empty State */}
            {!vendorBillsListLoading && viewMode === 'grid' && bills.length === 0 && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-300" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No bills found</p>
                </div>
            )}

            {/* List View */}
            {!vendorBillsListLoading && viewMode === 'list' && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                    <table className="tw-w-full tw-text-sm">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-px-3 tw-py-2 tw-w-16 tw-text-left tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase">
                                    Select
                                </th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Bill No</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Vendor</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Description</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Amount</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Bill Date</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Due Date</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Status</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Payment</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bills.map((bill) => (
                                <tr key={bill.id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50">
                                    <td className="tw-px-3 tw-py-2">
                                        {isDraft(bill.status) && (
                                            <DraftBillCheckbox
                                                checked={selectedIds.includes(bill.id)}
                                                onChange={() => toggleSelected(bill.id)}
                                                label={`Select draft bill ${bill.billNo}`}
                                            />
                                        )}
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <span className="tw-font-medium tw-text-blue-600">{bill.billNo}</span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <p className="tw-font-medium tw-text-gray-800">{bill.vendor}</p>
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-max-w-[200px] tw-truncate">{bill.description}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right">
                                        <p className="tw-font-semibold tw-text-gray-800">{formatCurrency(bill.totalAmount)}</p>
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{bill.billDate ? new Date(bill.billDate).toLocaleDateString('en-IN') : '-'}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{bill.dueDate ? new Date(bill.dueDate).toLocaleDateString('en-IN') : '-'}</td>
                                    <td className="tw-px-3 tw-py-2">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getStatusBadge(bill.status)}`}>
                                            {getStatusLabel(bill.status)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        {bill.status?.toLowerCase() === 'draft' ? (
                                            <span className="tw-text-gray-400">-</span>
                                        ) : (
                                            <div>
                                                <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getPaymentStatusBadge(bill.paymentStatus)}`}>
                                                    {bill.paymentStatus}
                                                </span>
                                                {bill.paymentStatus === 'Partially Paid' && (
                                                    <p className="tw-text-[10px] tw-text-gray-500 tw-mt-0.5">
                                                        {formatCurrency(bill.paidAmount)} of {formatCurrency(bill.totalAmount)}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                            <button onClick={() => handleViewBill(bill)} className="tw-p-1.5 tw-rounded hover:tw-bg-emerald-50 tw-text-gray-400 hover:tw-text-emerald-600" title="View">
                                                <Icon icon="mdi:eye-outline" />
                                            </button>
                                            <button onClick={() => handlePrintBill(bill)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="Print">
                                                <Icon icon="mdi:printer-outline" />
                                            </button>
                                            <button onClick={() => handleEditBill(bill)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600" title="Edit">
                                                <Icon icon="mdi:pencil-outline" />
                                            </button>
                                            {bill.status?.toLowerCase() === 'draft' && (
                                                <button onClick={() => handlePostBill(bill)} className="tw-p-1.5 tw-rounded hover:tw-bg-emerald-50 tw-text-gray-400 hover:tw-text-emerald-600" title="Post bill">
                                                    <Icon icon="mdi:check-circle-outline" />
                                                </button>
                                            )}
                                            {bill.status?.toLowerCase() === 'draft' && (
                                                <button onClick={() => handleDeleteBill(bill)} className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-600" title="Delete">
                                                    <Icon icon="mdi:delete-outline" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {bills.length === 0 && (
                        <div className="tw-text-center tw-py-8">
                            <Icon icon="mdi:file-document-outline" className="tw-text-3xl tw-text-gray-300" />
                            <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No bills found</p>
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {totalItems > 0 && (
                <div className="tw-mt-4">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={(value) => {
                            setItemsPerPage(value);
                            setCurrentPage(1);
                        }}
                        itemsPerPageOptions={[5, 10, 20, 50]}
                        itemLabel="bills"
                    />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {postModal.show && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-emerald-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:check-circle-outline" className="tw-text-emerald-600 tw-text-xl" />
                            </div>
                            <div>
                                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Post Bill</h3>
                                <p className="tw-text-sm tw-text-gray-500">The bill can then be paid</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">
                            Post bill <span className="tw-font-semibold">{postModal.bill?.billNo}</span>? A posted bill can no longer be deleted.
                        </p>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button
                                onClick={() => setPostModal({ show: false, bill: null })}
                                disabled={approveVendorBillLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmPostBill}
                                disabled={approveVendorBillLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-rounded-lg disabled:tw-opacity-50"
                            >
                                {approveVendorBillLoading ? 'Posting…' : 'Post Bill'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteModal.show && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:delete-alert" className="tw-text-red-600 tw-text-xl" />
                            </div>
                            <div>
                                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Delete Bill</h3>
                                <p className="tw-text-sm tw-text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">
                            Are you sure you want to delete bill <span className="tw-font-semibold">{deleteModal.bill?.billNo}</span>?
                        </p>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button
                                onClick={() => setDeleteModal({ show: false, bill: null })}
                                disabled={deleteVendorBillLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteBill}
                                disabled={deleteVendorBillLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50"
                            >
                                {deleteVendorBillLoading ? (
                                    <>
                                        <Icon icon="mdi:loading" className="tw-animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="mdi:delete" />
                                        Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Excel Upload — picker modal (bills) */}
            {uploadModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-md">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:file-excel-outline" className="tw-text-emerald-600" />
                                Bulk Upload Vendor Bills
                            </h3>
                            <button
                                onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
                                className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500"
                            >
                                <Icon icon="mdi:close" />
                            </button>
                        </div>
                        <div className="tw-p-4 tw-space-y-3">
                            <p className="tw-text-xs tw-text-gray-600">
                                One row per bill (single line item). Vendors are matched by Company Name.
                                Bill Amount is pre-tax; tax + net total are computed from the rate columns.
                            </p>
                            <button
                                type="button"
                                onClick={handleDownloadBillTemplate}
                                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-blue-700 hover:tw-underline"
                            >
                                <Icon icon="mdi:download" /> Download template
                            </button>
                            <label className="tw-block">
                                <span className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Excel file</span>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                    className="tw-block tw-w-full tw-text-xs tw-text-gray-700 file:tw-mr-3 file:tw-py-1.5 file:tw-px-3 file:tw-rounded file:tw-border-0 file:tw-bg-blue-50 file:tw-text-blue-700 hover:file:tw-bg-blue-100"
                                />
                                {uploadFile && (
                                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-1">
                                        Selected: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
                                    </p>
                                )}
                            </label>
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100">
                            <button
                                type="button"
                                onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
                                disabled={bulkUploadVendorBillsLoading}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-100 disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleUploadBillSubmit}
                                disabled={!uploadFile || bulkUploadVendorBillsLoading}
                                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-md disabled:tw-opacity-50"
                            >
                                {bulkUploadVendorBillsLoading && <Icon icon="mdi:loading" className="tw-animate-spin" />}
                                {bulkUploadVendorBillsLoading ? 'Uploading…' : 'Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Excel Upload — results modal (bills) */}
            {uploadResultOpen && bulkUploadVendorBillsResult && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-2xl tw-max-h-[85vh] tw-flex tw-flex-col">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:file-check-outline" className="tw-text-emerald-600" />
                                Upload Summary
                            </h3>
                            <button
                                onClick={handleCloseBillResults}
                                className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500"
                            >
                                <Icon icon="mdi:close" />
                            </button>
                        </div>
                        <div className="tw-p-4 tw-overflow-y-auto tw-flex-1">
                            <div className="tw-grid tw-grid-cols-3 tw-gap-2 tw-mb-4">
                                <div className="tw-rounded-md tw-bg-blue-50 tw-border tw-border-blue-100 tw-px-3 tw-py-2">
                                    <div className="tw-text-[10px] tw-text-blue-700 tw-uppercase tw-tracking-wide tw-font-semibold">Total</div>
                                    <div className="tw-text-lg tw-font-bold tw-text-blue-900">{bulkUploadVendorBillsResult.total}</div>
                                </div>
                                <div className="tw-rounded-md tw-bg-emerald-50 tw-border tw-border-emerald-100 tw-px-3 tw-py-2">
                                    <div className="tw-text-[10px] tw-text-emerald-700 tw-uppercase tw-tracking-wide tw-font-semibold">Created</div>
                                    <div className="tw-text-lg tw-font-bold tw-text-emerald-900">{bulkUploadVendorBillsResult.createdCount}</div>
                                </div>
                                <div className="tw-rounded-md tw-bg-rose-50 tw-border tw-border-rose-100 tw-px-3 tw-py-2">
                                    <div className="tw-text-[10px] tw-text-rose-700 tw-uppercase tw-tracking-wide tw-font-semibold">Failed</div>
                                    <div className="tw-text-lg tw-font-bold tw-text-rose-900">{bulkUploadVendorBillsResult.failedCount}</div>
                                </div>
                            </div>

                            {bulkUploadVendorBillsResult.created?.length > 0 && (
                                <div className="tw-mb-4">
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-emerald-700 tw-uppercase tw-tracking-wide tw-mb-1.5">Created</h4>
                                    <ul className="tw-space-y-0.5 tw-text-xs tw-text-gray-700 tw-max-h-40 tw-overflow-y-auto tw-border tw-border-gray-100 tw-rounded tw-p-2">
                                        {bulkUploadVendorBillsResult.created.map(r => (
                                            <li key={r.bill_id} className="tw-flex tw-items-center tw-gap-2">
                                                <span className="tw-text-gray-400 tw-font-mono tw-text-[10px] tw-w-8">#{r.row}</span>
                                                <Icon icon="mdi:check-circle" className="tw-text-emerald-500 tw-text-sm" />
                                                <span className="tw-font-medium">{r.bill_number}</span>
                                                <span className="tw-text-gray-500">— {r.vendor}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {bulkUploadVendorBillsResult.failed?.length > 0 && (
                                <div>
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-rose-700 tw-uppercase tw-tracking-wide tw-mb-1.5">Failed</h4>
                                    <ul className="tw-space-y-0.5 tw-text-xs tw-text-gray-700 tw-max-h-56 tw-overflow-y-auto tw-border tw-border-rose-100 tw-rounded tw-p-2">
                                        {bulkUploadVendorBillsResult.failed.map((r, i) => (
                                            <li key={i} className="tw-flex tw-items-start tw-gap-2">
                                                <span className="tw-text-gray-400 tw-font-mono tw-text-[10px] tw-w-8 tw-mt-0.5">#{r.row}</span>
                                                <Icon icon="mdi:alert-circle" className="tw-text-rose-500 tw-text-sm tw-mt-0.5 tw-flex-shrink-0" />
                                                <span>
                                                    {r.bill_number && <span className="tw-font-medium">{r.bill_number}: </span>}
                                                    <span className="tw-text-rose-700">{r.error}</span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-end tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100">
                            <button
                                onClick={handleCloseBillResults}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-md"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

export default VendorBill;
